// ⚠️ 開発用ページ：本番リリース前に削除してください（このファイルごと）。
// Supabase（Lovable Cloud）クライアントが正しく初期化され、
// バックエンドへ到達できるかを確認するための一時的な接続テストページです。

import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

type Status = "checking" | "success" | "error";

interface ConnectionResult {
  status: Status;
  message: string;
  detail?: string;
}

export const Route = createFileRoute("/test-connection")({
  head: () => ({
    meta: [
      { title: "接続テスト | KAGEM (開発用)" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TestConnectionPage,
});

function TestConnectionPage() {
  const [result, setResult] = useState<ConnectionResult>({
    status: "checking",
    message: "Lovable Cloud への接続を確認中...",
  });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        // セッション取得は未ログイン状態でも成功するため、クライアント初期化と
        // ネットワーク到達性を同時に検証できる軽量なヘルスチェックとして利用。
        const { error } = await supabase.auth.getSession();
        if (cancelled) return;

        if (error) {
          setResult({
            status: "error",
            message: "接続に失敗しました",
            detail: error.message,
          });
          return;
        }

        const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
        setResult({
          status: "success",
          message: "Lovable Cloud に正常に接続できました",
          detail: url ? `エンドポイント: ${url}` : undefined,
        });
      } catch (e) {
        if (cancelled) return;
        setResult({
          status: "error",
          message: "予期しないエラーが発生しました",
          detail: e instanceof Error ? e.message : String(e),
        });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="bg-gradient-hero relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 sm:px-6">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <BrandLogo size="md" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-xl backdrop-blur-md">
          <h1 className="text-lg font-semibold text-foreground">
            バックエンド接続テスト
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            このページは開発用です。本番では削除してください。
          </p>

          <div
            className={cn(
              "mt-6 flex items-start gap-3 rounded-xl border p-4",
              result.status === "checking" && "border-border bg-muted/40",
              result.status === "success" &&
                "border-emerald-500/30 bg-emerald-500/10",
              result.status === "error" &&
                "border-destructive/40 bg-destructive/10",
            )}
            role="status"
            aria-live="polite"
          >
            <StatusIcon status={result.status} />
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  "text-sm font-medium",
                  result.status === "success" && "text-emerald-600 dark:text-emerald-400",
                  result.status === "error" && "text-destructive",
                  result.status === "checking" && "text-foreground",
                )}
              >
                {result.message}
              </p>
              {result.detail && (
                <p className="mt-1 break-all text-xs text-muted-foreground">
                  {result.detail}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function StatusIcon({ status }: { status: Status }) {
  if (status === "checking") {
    return (
      <Loader2
        className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-muted-foreground"
        aria-hidden="true"
      />
    );
  }
  if (status === "success") {
    return (
      <CheckCircle2
        className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500"
        aria-hidden="true"
      />
    );
  }
  return (
    <XCircle
      className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
      aria-hidden="true"
    />
  );
}
